'use client';

import { c as _c } from "react/compiler-runtime";
import { HeroBanner } from '@/components/images/hero-banner';
import { UserAvatar } from '@/components/images/user-avatar';
import { UserIdentity } from '@/components/profile/user-identity';
import { PodiumRankMedal } from '@/components/social/leaderboard/podium-rank-medal';
import type { PodiumUserIdentity } from '@/components/social/types';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { onEvent } from '@/events';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProfileCardKpi {
  /** Pre-resolved (localised) column label rendered below the value. */
  label: string;
  /** Numeric value to animate toward. Callers convert D6 strings to floats. */
  value: number;
  /** Per-frame format callback. Use `formatBalance` for currency, `String` for counts. */
  format: (value: number) => string;
  /** Primary ranking metric for the active tab — uses signed feedback color. */
  isPrimary?: boolean;
}
export interface ProfileCardAnalyticsContext {
  /** Active social-page tab — emitted as `tab` on click events. */
  tab: 'leaderboard' | 'boosters' | 'creators' | 'backers';
  /** 1-based rank — emitted as `rank` on click events. */
  rank: 1 | 2 | 3;
}
export interface ProfileCardProps {
  /** Identity, hero URL, and connected-provider list for the card subject. */
  user: PodiumUserIdentity;
  /** Three primary KPIs (Profit / Boost Volume / Top Market). Order is preserved. */
  kpis: readonly [ProfileCardKpi, ProfileCardKpi, ProfileCardKpi];
  /** Where the avatar / name area link to (e.g. `/portfolio/<userId>`). */
  profileHref: string;
  /** Pre-resolved (localised) accessible name for the avatar profile link. */
  ctaLabel: string;
  /** Required so analytics events carry tab + rank context. */
  analyticsContext: ProfileCardAnalyticsContext;
  /** Accessible label for the rank medal on the hero (from `social.podium.medal.*`). */
  rankMedalAlt: string;
  /** Optional outer className — used by `<Podium>` for rank-1 elevation + mobile snap. */
  className?: string;
}

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const CARD_SHADOW_CLASS = 'shadow-elevation-card transition-shadow duration-fast ease-default hover:shadow-elevation-popover motion-reduce:transition-none' as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<ProfileCard>` — reusable profile-card surface used by the social podium.
 *
 * Composition (top → bottom):
 *  1. Hero strip (3:1 user-seeded `<HeroBanner>`) with a rank medal
 *     overlay top-right (gold / silver / bronze).
 *  2. 80 px avatar (horizontally centred) with a 4 px brand-surface ring
 *     overlapping the hero/body junction; doubles as a profile link.
 *  3. Username + handle (`<UserIdentity variant="stack">` with linked provider
 *     badges when present, centred under the avatar).
 *  4. Three KPI columns rendered through `<AnimatedNumber>` (tick-up on mount,
 *     reduced-motion snaps to final value via the primitive's built-in honour).
 *     Values use `text-foreground-primary` for a single readable hierarchy.
 *
 * Profile navigation: avatar and name rows link to `profileHref` (no separate
 * footer CTA).
 *
 * The hero region is decorative — `aria-hidden`, no anchor wrap.
 *
 * Rest state uses `shadow-elevation-card`; hover lifts to `shadow-elevation-popover`.
 * `motion-reduce` disables the shadow transition.
 *
 * The `// ALLOW-RAW-CARD` opt-out is required because `<Panel>` cannot express
 * the clipped hero strip + overlapping avatar + custom 3-column KPI grid in
 * one composition — this is the documented exception per the wrapper-rule
 * (frontend/.claude/rules/shadcn-ui.md → §"Per-site escape hatches").
 *
 * Spec: 071-social-podium-cards
 */
export function ProfileCard(t0) {
  const $ = _c(51);
  const {
    user,
    kpis,
    profileHref,
    ctaLabel,
    analyticsContext,
    rankMedalAlt,
    className
  } = t0;
  const targetUserId = user.userId ?? "";
  let t1;
  if ($[0] !== analyticsContext.rank || $[1] !== analyticsContext.tab || $[2] !== targetUserId) {
    t1 = function fireProfileClick(region) {
      onEvent("social_podium_card_clicked", {
        tab: analyticsContext.tab,
        rank: analyticsContext.rank,
        targetUserId,
        region
      });
    };
    $[0] = analyticsContext.rank;
    $[1] = analyticsContext.tab;
    $[2] = targetUserId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const fireProfileClick = t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("relative flex w-full flex-col overflow-hidden rounded-card border border-border-primary bg-background-primary", CARD_SHADOW_CLASS, className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = analyticsContext.rank;
  let t4;
  if ($[6] !== user.displayName || $[7] !== user.heroNsfw || $[8] !== user.heroUrl || $[9] !== user.userId) {
    t4 = <HeroBanner heroUrl={user.heroUrl} userId={user.userId} displayName={user.displayName} nsfw={user.heroNsfw} />;
    $[6] = user.displayName;
    $[7] = user.heroNsfw;
    $[8] = user.heroUrl;
    $[9] = user.userId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== analyticsContext.rank) {
    t5 = <PodiumRankMedal rank={analyticsContext.rank} alt="" />;
    $[11] = analyticsContext.rank;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className="relative" aria-hidden="true">{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== rankMedalAlt) {
    t7 = <span className="sr-only">{rankMedalAlt}</span>;
    $[16] = rankMedalAlt;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== fireProfileClick) {
    t8 = () => {
      fireProfileClick("avatar");
    };
    $[18] = fireProfileClick;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = user.avatarNsfw ?? false;
  const t10 = user.avatarPreferences?.rasterise ?? false;
  let t11;
  if ($[20] !== t10 || $[21] !== t9 || $[22] !== user.avatarUrl || $[23] !== user.displayName || $[24] !== user.userId) {
    t11 = <UserAvatar userId={user.userId} displayName={user.displayName} avatarUrl={user.avatarUrl} nsfw={t9} rasterise={t10} size="2xl" className="ring-4 ring-background-primary" />;
    $[20] = t10;
    $[21] = t9;
    $[22] = user.avatarUrl;
    $[23] = user.displayName;
    $[24] = user.userId;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== ctaLabel || $[27] !== profileHref || $[28] !== t11 || $[29] !== t8) {
    t12 = <div className="-mt-10 flex justify-center px-4"><Link href={profileHref} onClick={t8} aria-label={ctaLabel} className="inline-block shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t11}</Link></div>;
    $[26] = ctaLabel;
    $[27] = profileHref;
    $[28] = t11;
    $[29] = t8;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== fireProfileClick) {
    t13 = () => {
      fireProfileClick("name");
    };
    $[31] = fireProfileClick;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== user) {
    t14 = <UserIdentity user={user} variant="stack" size="md" showProviderBadges={true} handleClassName="text-foreground-tertiary" className="items-center text-center" />;
    $[33] = user;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== profileHref || $[36] !== t13 || $[37] !== t14) {
    t15 = <Link href={profileHref} onClick={t13} className="mt-2 flex min-w-0 w-full flex-col gap-0.5 px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t14}</Link>;
    $[35] = profileHref;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== kpis) {
    t16 = kpis.map(_temp);
    $[39] = kpis;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t16) {
    t17 = <ul className="mt-4 grid grid-cols-3 gap-2 px-4 pb-4 text-center">{t16}</ul>;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== analyticsContext.rank || $[44] !== t12 || $[45] !== t15 || $[46] !== t17 || $[47] !== t2 || $[48] !== t6 || $[49] !== t7) {
    t18 = <div className={t2} data-rank={t3}>{t6}{t7}{t12}{t15}{t17}</div>;
    $[43] = analyticsContext.rank;
    $[44] = t12;
    $[45] = t15;
    $[46] = t17;
    $[47] = t2;
    $[48] = t6;
    $[49] = t7;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  return t18;
}
function _temp(kpi) {
  return <li key={kpi.label} className="flex min-w-0 flex-col gap-0.5"><AnimatedNumber value={kpi.value} format={kpi.format} animateOnMount={true} className={cn("block tabular-nums text-body-lg font-bold", kpi.isPrimary === true ? kpi.value < 0 ? "text-feedback-error-content" : "text-feedback-success-content" : "text-foreground-primary")} /><span className={cn("block truncate text-caption font-medium", kpi.isPrimary === true ? kpi.value < 0 ? "text-feedback-error-content" : "text-feedback-success-content" : "text-foreground-tertiary")}>{kpi.label}</span></li>;
}